// Per-practice-area matter hero strip.
//
// Replaces ClientHero's right-side stat block with 3-5 stats specific to the
// active practice area. Each stat is resolved from the loose matter shape
// (matter / matter.matter / matter.workspace / matter.deadlines) so no
// backend changes are required — the schema is already there.
//
// Layout:
//   [avatar | client name + eID + status pills] [PA-specific stat blocks]
//   [team chips (the server's rows) ................. manage team, admin ]
//   [deadline tier strip — always-on, color-coded by urgency           ]
//
// Stats are config-driven by template id. Any PA missing a config
// gracefully shows ClientHero-style generics (next deadline / docs count).
// At <768px the stat row stacks vertically (see pa-theme.css).
//
// All visual chrome routes through the `--pa-accent*` CSS vars that
// MatterView sets on its container, so the avatar, accent line, and badge
// all tint to the practice area.

import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { adaptDeadlines, type MatterWithClient } from "../../api/mock";
import { initialsOf } from "../../api/auth";
import type { Client, Matter, PracticeArea } from "../../data/types";
import { practiceAreaLabel } from "../../data/practice-areas";
import { daysUntilDay, isDeadlineEvent, isPastEvent } from "../../api/firmDeadlines";
import { fmtDay, fmtDays } from "../../lib/dates";
import { closeDeadline } from "../../api/deadlines";
import { caseSignalsKey } from "../../hooks/useCaseSignals";
import {
  aNumberLabel,
  caseTypeLabel,
  stageLabel,
  statusLabel,
  teamRoleLabel,
} from "../../lib/caseLabels";
import { colorFor } from "../../lib/staffVisuals";
import { usePracticeAreaTemplate } from "../../hooks/usePracticeAreaTemplate";
import { useCurrentUser } from "../auth/CurrentUserContext";
import { isAdminRole } from "../../lib/roles";
import { EntityLink } from "../chrome/EntityLink";
import { EMPTY, SEP } from "../chrome/SepList";
import { TeamAssignmentEditor } from "../team/TeamAssignmentEditor";

interface Props {
  matter: Matter;
  client: Client | null | undefined;
  pa: PracticeArea | null;
  templateId: string | null;
}

interface StatBlock {
  label: string;
  value: string;
  /** Tint key — maps to pa-theme.css `.cc-matter-hero-stat[data-tone="..."]`. */
  tone?: "danger" | "warn" | "ok" | "accent" | "default";
  mono?: boolean;
}

// ---- helpers ---------------------------------------------------------------

function initialsFor(name: string | undefined): string {
  if (!name) return "?";
  const parts = name.split(/[ ,]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function pickWS<T>(matter: Matter, key: string): T | undefined {
  return matter.workspace?.[key] as T | undefined;
}

// Not lib/dates' fmtDate: a workspace bag is schemaless, so an attorney may
// have typed "C. 2015" where a date belongs, and that has to survive to the
// screen rather than collapse into a placeholder.
function fmtDateOrRaw(iso: string | undefined | null): string {
  if (!iso) return EMPTY;
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return fmtDay(iso);
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function fmtMoney(v: unknown): string {
  if (v == null || v === "") return "—";
  if (typeof v === "number")
    return v >= 0 ? `$${v.toLocaleString()}` : `-$${Math.abs(v).toLocaleString()}`;
  const s = String(v);
  // Already-formatted strings ("$120,000") pass through untouched.
  if (s.startsWith("$") || s.startsWith("-$")) return s;
  const n = Number(s.replace(/[$,]/g, ""));
  if (!Number.isNaN(n) && /^\$?[-\d,.]+$/.test(s)) return `$${n.toLocaleString()}`;
  return s;
}

// A stat labelled "Next hearing" or "Arraignment" shows that row or nothing.
// Standing the soonest unrelated deadline in for a missing one printed an
// appeal deadline as a hearing date; a hearing that went by is not the
// next one either.
function findDeadline(
  matter: Matter,
  needles: string[],
): { days: number; when: string; label: string } | null {
  const deadlines = matter.deadlines ?? [];
  for (const n of needles) {
    const k = n.toLowerCase();
    const hit = deadlines.find((d) => !isPastEvent(d) && d.label.toLowerCase().includes(k));
    if (hit) return { days: hit.days, when: hit.when, label: hit.label };
  }
  return null;
}

function hearingStat(m: Matter): StatBlock {
  const day = m.nextHearingDate;
  return {
    label: m.nextHearingKind === "interview" ? "Next interview" : "Next hearing",
    value: day ? fmtDay(day) : EMPTY,
    tone: day && daysUntilDay(day, true) <= 7 ? "danger" : "default",
  };
}

// The engine's own status word is not read: it calls a late filing safe.
function oneYearStat(m: Matter): StatBlock {
  const day = m.oneYearDeadline;
  if (!day) return { label: "1-year filing", value: EMPTY };
  if (m.oneYearFiled) return { label: "1-year filing", value: "Filed", tone: "ok" };
  const days = daysUntilDay(day, true);
  return {
    label: "1-year filing",
    value: fmtDays(days),
    tone: days < 90 ? "danger" : days < 180 ? "warn" : "ok",
  };
}

// An open row that has passed is still the row that matters most, and the
// word says so rather than the next one standing in for it; an event that
// went by is not that row.
function nextDeadlineStats(matter: Matter): StatBlock[] {
  const next = matter.deadlines?.find((d) => !isPastEvent(d));
  return [
    {
      label: next && next.days < 0 ? "Overdue" : "Next deadline",
      value: next ? next.label : EMPTY,
    },
    {
      label: "When",
      value: next ? fmtDateOrRaw(next.when) : EMPTY,
      tone:
        next?.days != null && next.days <= 7
          ? "danger"
          : next?.days != null && next.days <= 30
            ? "warn"
            : "default",
    },
  ];
}

// ---- per-template stat builders --------------------------------------------
//
// Each builder returns 3-5 stats. Always reads from the matter shape — no
// new backend fields. Returns the array in display order; first stat is the
// most "load-bearing" for that PA.

type StatBuilder = (matter: Matter) => StatBlock[];

const HERO_STATS: Record<string, StatBuilder> = {
  // ---------- Immigration ----------
  // Withholding and CAT share the I-589 track and this card; the one-year
  // bar is a fact only on an asylum claim.
  "immigration-asylum-defensive": (m) => {
    const aNum = m.matter?.aNumber;
    const venue = m.matter?.venue;
    const ead = pickWS<{ status?: string; currentDay?: number }>(m, "eadClock");
    const eadStatus = ead?.status ?? (ead?.currentDay != null ? `Day ${ead.currentDay}` : null);
    return [
      ...(m.subtype === "asylum" ? [oneYearStat(m)] : []),
      hearingStat(m),
      { label: "A-number", value: aNum ? aNumberLabel(aNum) : "—", mono: true },
      { label: "Venue", value: venue ?? "—" },
      { label: "EAD", value: eadStatus ?? "—", tone: "accent" },
    ];
  },

  "immigration-general": (m) => {
    const aNum = m.matter?.aNumber;
    return [
      hearingStat(m),
      ...nextDeadlineStats(m),
      { label: "A-number", value: aNum ? aNumberLabel(aNum) : EMPTY, mono: true },
      { label: "Docs", value: String(m.documents?.length ?? 0) },
    ];
  },

  "immigration-family-petition": (m) => {
    const i130 = pickWS<{ filed?: string; pendingMonths?: number }>(m, "i130");
    const priorityDate =
      pickWS<string>(m, "priorityDate") ??
      (m.matter as { priorityDate?: string } | undefined)?.priorityDate;
    const nvc = pickWS<{ stage?: string }>(m, "nvc")?.stage ?? pickWS<string>(m, "nvcStage");
    const beneficiary = pickWS<{ inUS?: boolean; country?: string }>(m, "beneficiary");
    const serviceCenter =
      pickWS<string>(m, "serviceCenter") ?? (m.matter as { venue?: string } | undefined)?.venue;
    const pendingMonths =
      i130?.pendingMonths ??
      (i130?.filed ? Math.floor((Date.now() - Date.parse(i130.filed)) / (30 * 86_400_000)) : null);
    return [
      {
        label: "I-130 pending",
        value: pendingMonths != null ? `${pendingMonths} mo` : "—",
        tone: "accent",
      },
      {
        label: "Priority date",
        value: priorityDate ? fmtDateOrRaw(priorityDate) : "—",
      },
      { label: "Service center", value: serviceCenter ?? "—" },
      {
        label: "Beneficiary",
        value: beneficiary?.inUS == null ? "—" : beneficiary.inUS ? "In US" : "Abroad",
      },
      { label: "NVC stage", value: nvc ?? "—" },
    ];
  },

  // ---------- Criminal ----------
  "criminal-defense": (m) => {
    const next = findDeadline(m, ["court", "trial", "preliminary", "arraignment"]);
    const charges = pickWS<Array<{ statute?: string; degree?: string }>>(m, "charges") ?? [];
    const maxExposure =
      pickWS<{ years?: number; months?: number }>(m, "maxExposure") ??
      pickWS<{ years?: number }>(m, "sentencing");
    const exposureStr =
      maxExposure?.years != null
        ? `${maxExposure.years} yr${maxExposure.years === 1 ? "" : "s"}`
        : "—";
    const bail = pickWS<{ status?: string; amount?: number | string }>(m, "bail");
    const padilla = pickWS<{ status?: string; flag?: string }>(m, "immigrationImpact");
    return [
      {
        label: "Next court",
        value: next ? `${fmtDateOrRaw(next.when)} · ${fmtDays(next.days)}` : "—",
        tone: next?.days != null && next.days <= 7 ? "danger" : "default",
      },
      {
        label: "Charges",
        value:
          charges.length > 0 ? `${charges.length} count${charges.length === 1 ? "" : "s"}` : "—",
      },
      { label: "Max exposure", value: exposureStr, tone: "warn" },
      { label: "Bail", value: bail?.status ? bail.status : "—" },
      {
        label: "Padilla",
        value: padilla?.flag ?? padilla?.status ?? "—",
        tone: padilla?.flag ? "danger" : "accent",
      },
    ];
  },

  dui: (m) => {
    const arraignment = findDeadline(m, ["arraignment", "court", "trial"]);
    const bac = pickWS<string | number>(m, "bacReading");
    const license = pickWS<{ status?: string; suspended?: boolean }>(m, "license");
    const priors = pickWS<number>(m, "priorOffenses") ?? pickWS<number>(m, "offenseNumber") ?? 0;
    const iid = pickWS<boolean>(m, "iidRequired");
    return [
      {
        label: "Arraignment",
        value: arraignment ? fmtDateOrRaw(arraignment.when) : "—",
        tone: arraignment?.days != null && arraignment.days <= 7 ? "danger" : "default",
      },
      { label: "BAC", value: bac != null ? String(bac) : EMPTY, tone: "warn" },
      {
        label: "License",
        value: license?.suspended ? "Suspended" : (license?.status ?? "—"),
        tone: license?.suspended ? "danger" : "default",
      },
      { label: "Priors", value: priors != null ? `${priors}` : EMPTY },
      {
        label: "IID",
        value: iid == null ? "—" : iid ? "Required" : "Not required",
        tone: iid ? "warn" : "default",
      },
    ];
  },

  traffic: (m) => {
    const courtDate = findDeadline(m, ["court", "hearing"]);
    const points = pickWS<number | string>(m, "pointsExposure");
    const cdl = pickWS<boolean>(m, "cdlImpacted");
    const attendance = pickWS<string>(m, "attendanceStatus") ?? pickWS<string>(m, "appearance");
    return [
      {
        label: "Court date",
        value: courtDate ? fmtDateOrRaw(courtDate.when) : "—",
        tone: courtDate?.days != null && courtDate.days <= 7 ? "danger" : "default",
      },
      { label: "Points", value: points != null ? String(points) : EMPTY, tone: "warn" },
      {
        label: "CDL impact",
        value: cdl == null ? "—" : cdl ? "Yes" : "No",
        tone: cdl ? "danger" : "default",
      },
      { label: "Attendance", value: attendance ?? "—" },
    ];
  },

  // ---------- Personal Injury / Med Mal ----------
  "personal-injury-auto": (m) => {
    const sol = findDeadline(m, ["statute of limitations", "sol"]);
    const treatment = pickWS<{ status?: string; activeProviders?: number }>(m, "treatment");
    const treatmentStatus = treatment?.status ?? pickWS<string>(m, "treatmentStatus");
    const demand =
      pickWS<{ amount?: number | string; sent?: string }>(m, "demand") ??
      pickWS<{ amount?: number | string }>(m, "demandValue");
    const liens = pickWS<{ total?: number | string }>(m, "liens");
    const offer =
      pickWS<{ amount?: number | string }>(m, "offer") ??
      pickWS<{ amount?: number | string }>(m, "settlement");
    return [
      {
        label: "SOL",
        value: sol?.days != null ? fmtDays(sol.days) : "—",
        tone:
          sol?.days != null && sol.days <= 90
            ? "danger"
            : sol?.days != null && sol.days <= 180
              ? "warn"
              : "ok",
      },
      { label: "Treatment", value: treatmentStatus ?? "—", tone: "accent" },
      { label: "Demand", value: fmtMoney(demand?.amount), tone: "accent" },
      { label: "Liens", value: fmtMoney(liens?.total) },
      { label: "Offer", value: fmtMoney(offer?.amount), tone: "ok" },
    ];
  },

  medmal: (m) => {
    const sol = findDeadline(m, ["statute of limitations", "statute of repose", "sol", "sor"]);
    const expert = pickWS<{ engaged?: boolean; specialty?: string }>(m, "expert");
    const damages =
      pickWS<{ estimated?: number | string }>(m, "damages") ??
      pickWS<{ estimatedDamages?: number | string }>(m, "estimatedDamages");
    const carrier =
      pickWS<{ name?: string }>(m, "defendantCarrier") ?? pickWS<string>(m, "carrier");
    return [
      {
        label: "SOL / repose",
        value: sol?.days != null ? fmtDays(sol.days) : "—",
        tone:
          sol?.days != null && sol.days <= 60
            ? "danger"
            : sol?.days != null && sol.days <= 180
              ? "warn"
              : "default",
      },
      {
        label: "Expert",
        value:
          expert?.engaged == null
            ? "—"
            : expert.engaged
              ? `Engaged${expert.specialty ? " · " + expert.specialty : ""}`
              : "Not engaged",
        tone: expert?.engaged ? "ok" : "warn",
      },
      {
        label: "Damages est.",
        value: fmtMoney(
          typeof damages === "object" && damages != null
            ? "estimated" in damages
              ? damages.estimated
              : (damages as { estimatedDamages?: string | number }).estimatedDamages
            : damages,
        ),
        tone: "accent",
      },
      { label: "Carrier", value: typeof carrier === "string" ? carrier : (carrier?.name ?? "—") },
    ];
  },

  // ---------- Estates ----------
  "estate-wills-probate": (m) => {
    const dod = pickWS<string>(m, "dateOfDeath");
    const stage = pickWS<string>(m, "probateStage");
    const value = pickWS<number | string>(m, "estateValue");
    const beneficiaries = pickWS<unknown[]>(m, "beneficiaries");
    const contest = pickWS<{ status?: string; active?: boolean }>(m, "contest");
    return [
      { label: "Date of death", value: fmtDateOrRaw(dod) },
      { label: "Probate stage", value: stageLabel(stage ?? m.stage) || "—", tone: "accent" },
      { label: "Estate value", value: fmtMoney(value), tone: "accent" },
      {
        label: "Beneficiaries",
        value: Array.isArray(beneficiaries) ? `${beneficiaries.length}` : "—",
      },
      {
        label: "Contest",
        value: contest?.active ? "Active" : (contest?.status ?? "None"),
        tone: contest?.active ? "warn" : "default",
      },
    ];
  },

  // ---------- Family ----------
  "family-law": (m) => {
    const next = findDeadline(m, ["court", "hearing", "trial", "mediation"]);
    const stage = pickWS<string>(m, "caseStage") ?? m.stage;
    const custody =
      pickWS<{ schedule?: string }>(m, "custody")?.schedule ?? pickWS<string>(m, "custodySchedule");
    const support = pickWS<{ amount?: number | string; type?: string }>(m, "support");
    const children = pickWS<unknown[]>(m, "children") ?? pickWS<unknown[]>(m, "minorChildren");
    return [
      { label: "Stage", value: stageLabel(stage) || "—", tone: "accent" },
      {
        label: "Next hearing",
        value: next ? fmtDateOrRaw(next.when) : "—",
        tone: next?.days != null && next.days <= 14 ? "warn" : "default",
      },
      { label: "Custody", value: custody ?? "—" },
      {
        label: "Support",
        value:
          support?.amount != null ? `${fmtMoney(support.amount)}${support.type ? "/mo" : ""}` : "—",
      },
      {
        label: "Children",
        value: Array.isArray(children) ? `${children.length}` : "—",
      },
    ];
  },

  // ---------- SSI ----------
  ssi: (m) => {
    const hearing = findDeadline(m, ["alj", "hearing"]);
    const alj = pickWS<{ name?: string }>(m, "alj")?.name ?? pickWS<string>(m, "aljName");
    const onset = pickWS<string>(m, "onsetDate");
    const listings = pickWS<unknown[]>(m, "listings");
    const work = pickWS<{ years?: number }>(m, "workHistory");
    return [
      {
        label: "Hearing",
        value: hearing ? fmtDateOrRaw(hearing.when) : "—",
        tone: hearing?.days != null && hearing.days <= 14 ? "warn" : "default",
      },
      { label: "ALJ", value: alj ?? "—" },
      { label: "Onset", value: fmtDateOrRaw(onset) },
      {
        label: "Listings",
        value: Array.isArray(listings) ? `${listings.length} analyzed` : "—",
        tone: "accent",
      },
      { label: "Work history", value: work?.years != null ? `${work.years} yrs` : EMPTY },
    ];
  },

  // ---------- Civil litigation ----------
  "civil-litigation": (m) => {
    const phase = pickWS<string>(m, "caseStage") ?? m.stage;
    const next = m.deadlines?.find((d) => !isPastEvent(d));
    const demand = pickWS<{ amount?: number | string }>(m, "demand");
    const offer = pickWS<{ amount?: number | string }>(m, "offer");
    const aic = pickWS<number | string>(m, "amountInControversy");
    return [
      { label: "Phase", value: stageLabel(phase) || "—", tone: "accent" },
      {
        label: "Next deadline",
        value: next ? `${next.label.split("—")[0].trim()} · ${fmtDateOrRaw(next.when)}` : "—",
        tone: next?.days != null && next.days <= 7 ? "danger" : "default",
      },
      { label: "Demand", value: fmtMoney(demand?.amount) },
      { label: "Offer", value: fmtMoney(offer?.amount), tone: "ok" },
      { label: "Amt. in controversy", value: fmtMoney(aic) },
    ];
  },

  // ---------- Appeals ----------
  appeals: (m) => {
    const briefDue = findDeadline(m, ["brief", "opening", "reply"]);
    const oralArg = findDeadline(m, ["oral arg", "argument"]);
    const isReply = pickWS<boolean>(m, "isReply") ?? false;
    const court = pickWS<string>(m, "appellateCourt") ?? m.matter?.venue;
    const docket = pickWS<string>(m, "docketNumber");
    return [
      {
        label: "Brief due",
        value: briefDue?.days != null ? fmtDays(briefDue.days) : "—",
        tone:
          briefDue?.days != null && briefDue.days <= 14
            ? "danger"
            : briefDue?.days != null && briefDue.days <= 30
              ? "warn"
              : "default",
      },
      { label: "Brief type", value: isReply ? "Reply" : "Opening", tone: "accent" },
      { label: "Court", value: court ?? "—" },
      { label: "Docket", value: docket ?? "—", mono: true },
      { label: "Oral arg", value: oralArg ? fmtDateOrRaw(oralArg.when) : "—" },
    ];
  },

  // ---------- Bankruptcy ----------
  bankruptcy: (m) => {
    const chapter = pickWS<string | number>(m, "chapter") ?? m.subtype;
    const meeting = findDeadline(m, ["341", "meeting of creditors"]);
    const planStatus = pickWS<string>(m, "planStatus");
    const dischargeable = pickWS<boolean>(m, "dischargeable");
    return [
      {
        label: "Chapter",
        value: chapter != null ? `Ch ${String(chapter).replace(/^chapter-/i, "")}` : "—",
        tone: "accent",
      },
      {
        label: "341 meeting",
        value: meeting ? fmtDateOrRaw(meeting.when) : "—",
        tone: meeting?.days != null && meeting.days <= 14 ? "warn" : "default",
      },
      { label: "Plan", value: planStatus ?? "—" },
      {
        label: "Dischargeable",
        value: dischargeable == null ? "—" : dischargeable ? "Yes" : "No",
        tone: dischargeable === false ? "danger" : "default",
      },
    ];
  },

  // ---------- Municipal ----------
  "municipal-court": (m) => {
    const hearing = findDeadline(m, ["court", "hearing", "appearance"]);
    const charge = pickWS<string>(m, "chargeType") ?? m.subtype;
    const court = pickWS<string>(m, "localCourt") ?? m.matter?.venue;
    const docket = pickWS<string>(m, "docketNumber");
    return [
      {
        label: "Hearing",
        value: hearing ? fmtDateOrRaw(hearing.when) : "—",
        tone: hearing?.days != null && hearing.days <= 7 ? "danger" : "default",
      },
      { label: "Charge type", value: charge ?? "—", tone: "accent" },
      { label: "Court", value: court ?? "—" },
      { label: "Docket", value: docket ?? "—", mono: true },
    ];
  },
};

// Generic fallback when the template id has no custom builder.
function genericStats(matter: Matter): StatBlock[] {
  return [
    ...nextDeadlineStats(matter),
    { label: "Stage", value: stageLabel(matter.stage) || "—", tone: "accent" },
    { label: "Docs", value: String(matter.documents?.length ?? 0) },
  ];
}

/** Exported for the stat tests - the builders are the interesting part. */
export function statsFor(matter: Matter, templateId: string | null): StatBlock[] {
  const builder = templateId ? HERO_STATS[templateId] : null;
  return builder ? builder(matter) : genericStats(matter);
}

// ---- component -------------------------------------------------------------

type CloseNote = { seq: number; text: string; bad: boolean };

// Closing a row is a write kept out of the strip's render: the strip
// stays a view of the matter it was given, and the row leaves it when
// the server's answer says so.
function CloseDeadlineButton({
  caseId,
  row,
  onResult,
}: {
  caseId: string;
  row: { label: string; day: string };
  onResult: (note: { text: string; bad: boolean }) => void;
}) {
  const queryClient = useQueryClient();
  // isPending stays false until the next render; the ref stops a second
  // dispatch that lands before React re-renders the disabled button.
  const inFlight = useRef(false);
  const close = useMutation({
    mutationFn: () => closeDeadline(caseId, row),
    // The answer is the strip's truth: the row leaves with it, and a twin
    // the write did not reach comes back as a fresh Done. The reads behind
    // the counts follow on their own; a refetch is cancelled by the next
    // one, so the button never waits on it.
    onSuccess: async (rows) => {
      // A read of this matter still out would land over the answer once
      // the page is left; nothing it brings is newer than the answer.
      await queryClient.cancelQueries({
        queryKey: ["matter"],
        predicate: (q) => (q.state.data as MatterWithClient | null | undefined)?.mongoId === caseId,
      });
      queryClient.setQueriesData<MatterWithClient | null>({ queryKey: ["matter"] }, (old) =>
        old && old.mongoId === caseId ? { ...old, deadlines: adaptDeadlines(caseId, rows) } : old,
      );
      onResult({ text: `Closed ${row.label}`, bad: false });
    },
    onError: (err) => onResult({ text: `Could not close ${row.label}: ${err.message}`, bad: true }),
    onSettled: () => {
      inFlight.current = false;
      void queryClient.invalidateQueries({ queryKey: ["matter"] });
      void queryClient.invalidateQueries({ queryKey: ["matters"] });
      void queryClient.invalidateQueries({ queryKey: ["firm"] });
      void queryClient.invalidateQueries({ queryKey: ["client"] });
      void queryClient.invalidateQueries({ queryKey: caseSignalsKey(caseId) });
    },
  });
  const click = () => {
    if (inFlight.current) return;
    inFlight.current = true;
    close.mutate();
  };
  const word = close.isPending ? "Closing..." : close.isError ? "Try again" : "Done";
  return (
    <button
      type="button"
      className="cc-link-btn cc-matter-hero-deadline-done"
      disabled={close.isPending}
      onClick={click}
    >
      {word}
      <span className="sr-only"> {row.label}</span>
    </button>
  );
}

export function MatterHero({ matter, client, pa, templateId }: Props) {
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  // What the last close did on each matter, said under the strip: a row
  // that just leaves tells assistive tech nothing. Kept per matter, since
  // a cached matter re-renders this hero in place and a late answer may
  // belong to the matter before; the count makes the same words said
  // twice a fresh node, which a live region announces.
  const [closeNotes, setCloseNotes] = useState<Record<string, CloseNote>>({});
  // The team route is admin-only; nobody else gets a button that cannot save.
  const isAdmin = isAdminRole(useCurrentUser()?.role);
  const stats = statsFor(matter, templateId);
  const subtypeBadge = matter.subtype ?? matter.matter?.subtype;
  // The practice area may have its own word for a status ("Awaiting decision").
  const words = usePracticeAreaTemplate(templateId).data?.ui?.status_terminology;
  const status = matter.serverStatus ?? matter.status;
  const tone =
    status === "active" ? "ok" : status === "closed" || status === "archived" ? "muted" : "warn";

  const team = matter.team ?? [];
  const caseId = (matter as Partial<MatterWithClient>).mongoId;
  const shownNote = caseId ? (closeNotes[caseId] ?? null) : null;
  const matterDocId = caseId ?? matter.eID;

  return (
    <div className="cc-card cc-matter-hero">
      <div className="cc-matter-hero-row">
        <span className="cc-matter-hero-avatar">{initialsFor(client?.name)}</span>
        <div className="cc-matter-hero-id">
          <div className="cc-matter-hero-name">
            {client ? (
              <EntityLink kind="client" id={client.id} subtle>
                {client.name}
              </EntityLink>
            ) : (
              <span>(unknown client)</span>
            )}
            <span className="cc-matter-hero-eid">{matter.eID}</span>
            {pa && (
              <span className="cc-matter-hero-pa-badge" title={pa.tagline}>
                <span className="pa-dot" aria-hidden="true" />
                {pa.label}
                {subtypeBadge && (
                  <span className="cc-matter-hero-pa-subtype">
                    {" "}
                    · {caseTypeLabel(subtypeBadge)}
                  </span>
                )}
              </span>
            )}
            {matter.needsAttention && (
              <span className="cc-matter-hero-pill danger">Needs attention</span>
            )}
            {matter.needsReview ? (
              // A draft is under review whatever status it carries.
              <span className="cc-matter-hero-pill warn">awaiting review</span>
            ) : (
              <span className={`cc-matter-hero-pill ${tone}`}>
                {words?.[status] ?? statusLabel(status)}
              </span>
            )}
          </div>
          <div className="cc-matter-hero-meta">
            {pa ? (
              <EntityLink kind="practiceArea" id={pa.id} subtle>
                {pa.label}
              </EntityLink>
            ) : (
              practiceAreaLabel(matter.practiceArea)
            )}
            {matter.matter?.aNumber ? (
              <>
                {SEP}
                <span className="tnum-mono">{aNumberLabel(matter.matter.aNumber)}</span>
              </>
            ) : (
              ""
            )}
            {matter.matter?.venue ? ` · ${matter.matter.venue}` : ""}
            {` · ${stageLabel(matter.matter?.stage ?? matter.stage)}`}
          </div>
        </div>
        <div className="cc-matter-hero-stats">
          {stats.map((s, i) => (
            <div
              key={`${s.label}-${i}`}
              className="cc-matter-hero-stat"
              data-tone={s.tone ?? "default"}
            >
              <div className="cc-matter-hero-stat-label">{s.label}</div>
              <div className={`cc-matter-hero-stat-value${s.mono ? " mono" : ""}`} title={s.value}>
                {s.value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Team chips row: a row that names an account links to the person's page. */}
      <div className="cc-matter-hero-team">
        <span className="cc-matter-hero-team-label">Team</span>
        {team.length === 0 ? (
          <span className="cc-matter-hero-team-empty">No one assigned yet</span>
        ) : (
          team.map((m, i) => {
            const chip = (
              <>
                <span
                  className="cc-matter-hero-team-avatar"
                  style={{ background: m.userId ? colorFor(m.userId) : "var(--muted)" }}
                  aria-hidden="true"
                >
                  {initialsOf(m.name)}
                </span>
                <span className="cc-matter-hero-team-role">{teamRoleLabel(m.role)}:</span>
                <span>{m.name}</span>
              </>
            );
            return m.userId ? (
              <Link key={i} to={`/team/${m.userId}`} className="cc-matter-hero-team-chip">
                {chip}
              </Link>
            ) : (
              <span key={i} className="cc-matter-hero-team-chip">
                {chip}
              </span>
            );
          })
        )}
        {isAdmin && (
          <button type="button" className="team-manage-btn" onClick={() => setTeamModalOpen(true)}>
            Manage team
          </button>
        )}
      </div>

      {isAdmin && teamModalOpen && (
        <TeamAssignmentEditor
          matter={matter as MatterWithClient}
          matterDocId={matterDocId}
          onClose={() => setTeamModalOpen(false)}
        />
      )}

      {/* Deadline-tier strip */}
      {(matter.deadlines ?? []).length > 0 && (
        <div className="cc-matter-hero-deadlines">
          {(matter.deadlines ?? []).slice(0, 5).map((d) => {
            // The bucket carries the server's word on a hard clock; an event
            // that went by is history, in the muted tone.
            const past = isPastEvent(d);
            const tone = past
              ? "default"
              : d.days <= 7 || d.bucket === "critical"
                ? "danger"
                : d.days <= 30
                  ? "warn"
                  : "ok";
            const tier = past
              ? "passed"
              : d.days < 0
                ? "overdue"
                : tone === "danger"
                  ? "critical"
                  : tone === "warn"
                    ? "soon"
                    : "tracked";
            return (
              <div
                key={d.id}
                className="cc-matter-hero-deadline"
                data-tone={tone}
                title={`${d.label} — ${d.when}`}
              >
                <div className="cc-matter-hero-deadline-tier">
                  {tier} · {fmtDays(d.days)}
                </div>
                <div className="cc-matter-hero-deadline-label">{d.label}</div>
                {caseId && d.day && !isDeadlineEvent(d.type) && (
                  <CloseDeadlineButton
                    caseId={caseId}
                    row={{ label: d.label, day: d.day }}
                    onResult={(note) =>
                      setCloseNotes((prev) => ({
                        ...prev,
                        [caseId]: { ...note, seq: (prev[caseId]?.seq ?? 0) + 1 },
                      }))
                    }
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
      <span className={"cc-matter-hero-note" + (shownNote?.bad ? " bad" : "")} role="status">
        {shownNote && <span key={`${caseId}:${shownNote.seq}`}>{shownNote.text}</span>}
      </span>
    </div>
  );
}
